import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context';
import api from '../api/client';
import i18n from '../i18n';

// ── Metaduomenys (label = i18n raktas) ──────────────────────────────────────

export const CONDITION_META = {
  new:         { label: 'listings.cond_new',         icon: '✨', color: '#44aa66' },
  used:        { label: 'listings.cond_used',        icon: '🎲', color: '#4488cc' },
  old:         { label: 'listings.cond_old',         icon: '📦', color: '#a08040' },
  retro:       { label: 'listings.cond_retro',       icon: '🕹️', color: '#cc7744' },
  collectible: { label: 'listings.cond_collectible', icon: '💎', color: '#9b59b6' },
};

export const CURRENCY_META = {
  EUR: { symbol: '€', label: 'EUR €' },
  USD: { symbol: '$', label: 'USD $' },
  GBP: { symbol: '£', label: 'GBP £' },
};

export const LISTING_TYPE_META = {
  sale:     { label: 'listings.type_sale',     icon: '💰', color: '#44aa66' },
  exchange: { label: 'listings.type_exchange', icon: '🔄', color: '#4488cc' },
  auction:  { label: 'listings.type_auction',  icon: '🔨', color: '#cc7744' },
};

// „Liko 3 d. 4 val." – kiek liko iki aukciono pabaigos
export function timeLeftLt(endStr) {
  if (!endStr) return '';
  const diff = new Date(endStr.replace(' ', 'T')).getTime() - Date.now();
  if (diff <= 0) return i18n.t('listings.auction_ended');
  const mins  = Math.floor(diff / 60000);
  const days  = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  if (days > 0)  return i18n.t('listings.left_dh', { d: days, h: hours });
  if (hours > 0) return i18n.t('listings.left_hm', { h: hours, m: mins % 60 });
  return i18n.t('listings.left_m', { m: mins });
}

export function formatPrice(price, currency) {
  const sym = CURRENCY_META[currency]?.symbol || currency;
  const n = Number(price) || 0;
  return `${n % 1 === 0 ? n : n.toFixed(2)} ${sym}`;
}

// "prieš 9 d." – santykinis laikas per i18n
export function timeAgoLt(dateStr) {
  if (!dateStr) return '';
  const then = new Date(dateStr.replace(' ', 'T') + (dateStr.includes('Z') ? '' : 'Z'));
  const diffMs = Date.now() - then.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return i18n.t('listings.just_now');
  const hours = Math.floor(mins / 60);
  if (hours < 24) return i18n.t('listings.ago_h', { n: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return i18n.t('listings.ago_d', { n: days });
  const months = Math.floor(days / 30);
  return i18n.t('listings.ago_mo', { n: months });
}

function TypeBadge({ type, size = 12 }) {
  const { t } = useTranslation();
  const m = LISTING_TYPE_META[type];
  if (!m || type === 'sale') return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: size, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: m.color + '22', border: `1px solid ${m.color}55`, color: m.color }}>
      {m.icon} {t(m.label)}
    </span>
  );
}

function ConditionBadge({ condition, size = 12 }) {
  const { t } = useTranslation();
  const m = CONDITION_META[condition] || { label: condition, icon: '❓', color: '#888' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: size, fontWeight: 700, padding: '2px 10px', borderRadius: 6, background: m.color + '22', border: `1px solid ${m.color}55`, color: m.color }}>
      {m.icon} {t(m.label)}
    </span>
  );
}

const PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="100%" height="100%" fill="#2a2440"/><text x="50%" y="50%" fill="#7b6ba8" font-size="48" text-anchor="middle" dominant-baseline="middle">🎲</text></svg>'
);

// ── Nuotraukų įkėlimas: drag&drop + galerija su pertvarkymu ─────────────────

function ImageUploader({ images, onChange }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const dragIndex = useRef(null);
  const fileRef = useRef(null);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith('image/'));
    if (!files.length) return;
    if (images.length + files.length > 12) {
      setError(t('listings.max_images')); return;
    }
    setUploading(true); setError('');
    const added = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post('/upload', fd, { timeout: 60000 });
        added.push(r.data.url);
      } catch (e) {
        setError(e.response?.data?.detail || t('listings.upload_failed', { name: file.name }));
      }
    }
    if (added.length) onChange([...images, ...added]);
    setUploading(false);
  };

  const reorder = (from, to) => {
    if (from === null || from === to) return;
    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault(); setDragOver(false);
          if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? '#f0c040' : 'var(--border2)'}`,
          background: dragOver ? 'rgba(240,192,64,0.08)' : 'var(--bg)',
          borderRadius: 12, padding: '26px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all .18s',
        }}
      >
        <div style={{ fontSize: 30, marginBottom: 6 }}>📷</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 600 }}>
          {uploading ? `⏳ ${t('listings.uploading')}` : t('listings.drop_hint')}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
          {t('listings.drop_sub')}
        </div>
        <input
          ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
          onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }}
        />
      </div>
      {error && <div style={{ color: '#ff6060', fontSize: 12, marginTop: 8 }}>⚠️ {error}</div>}

      {images.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
          {images.map((url, i) => (
            <div
              key={url + i}
              draggable
              onDragStart={() => { dragIndex.current = i; }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); e.stopPropagation(); reorder(dragIndex.current, i); dragIndex.current = null; }}
              style={{ position: 'relative', width: 96, height: 96, borderRadius: 10, overflow: 'hidden', border: i === 0 ? '2px solid #f0c040' : '1px solid var(--border2)', cursor: 'grab' }}
              title={t('listings.drag_reorder')}
            >
              <img src={url} alt={`${i + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {i === 0 && (
                <span style={{ position: 'absolute', left: 4, bottom: 4, background: 'rgba(240,192,64,0.9)', color: '#201a10', fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 4 }}>{t('listings.main_photo').toUpperCase()}</span>
              )}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onChange(images.filter((_, x) => x !== i)); }}
                style={{ position: 'absolute', top: 3, right: 3, background: 'rgba(0,0,0,0.65)', border: 'none', color: '#ff8080', borderRadius: 6, width: 22, height: 22, cursor: 'pointer', fontSize: 12, lineHeight: 1 }}
              >✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Skelbimo forma (kūrimas / redagavimas) ──────────────────────────────────

const EMPTY_FORM = {
  title: '', year: '', description: '', price: '', currency: 'EUR',
  condition: 'used', city: '', country: 'Lietuva', bgg_url: '', shipping_info: '', images: [],
  listing_type: 'sale', exchange_wish: '', auction_end: '',
};

export function ListingForm({ initial, onSaved, onCancel }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => initial ? {
    ...EMPTY_FORM, ...initial,
    year: initial.year || '', price: initial.price ?? '',
    images: Array.isArray(initial.images) ? initial.images : [],
    listing_type: initial.listing_type || 'sale',
    exchange_wish: initial.exchange_wish || '',
    auction_end: initial.auction_end ? initial.auction_end.replace(' ', 'T').slice(0, 16) : '',
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isExchange = form.listing_type === 'exchange';
  const isAuction  = form.listing_type === 'auction';
  const priceLabel = isAuction ? t('listings.price_start') : isExchange ? t('listings.price_value') : t('listings.price_label');

  const save = async () => {
    if (!form.title.trim()) { setError(t('listings.err_title_required')); return; }
    const priceRequired = !isExchange;
    if ((priceRequired && form.price === '') || isNaN(Number(form.price || 0)) || Number(form.price || 0) < 0) {
      setError(t('listings.err_price')); return;
    }
    if (isAuction && !form.auction_end) { setError(t('listings.err_auction_end')); return; }
    if (isExchange && !form.exchange_wish.trim()) { setError(t('listings.err_exchange_wish')); return; }
    setSaving(true); setError('');
    const payload = {
      ...form,
      year: form.year ? Number(form.year) : null,
      price: Number(form.price || 0),
    };
    try {
      const r = initial?.id
        ? await api.put(`/listings/${initial.id}`, payload)
        : await api.post('/listings', payload);
      onSaved(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('listings.err_save'));
    } finally { setSaving(false); }
  };

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid rgba(240,192,64,0.25)', borderRadius: 12, padding: '20px 24px', marginBottom: 24 }}>
      <div style={{ fontFamily: 'Cinzel,serif', fontSize: 15, color: 'var(--gold2)', marginBottom: 16 }}>
        {initial?.id ? `✏️ ${t('listings.edit_title')}` : `🎲 ${t('listings.new_title')}`}
      </div>

      {/* Skelbimo tipas: parduoti / mainai / aukcionas */}
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">{t('listings.field_type')} *</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {Object.entries(LISTING_TYPE_META).map(([k, m]) => (
            <button
              key={k} type="button"
              onClick={() => set('listing_type', k)}
              style={{
                background: form.listing_type === k ? m.color + '22' : 'var(--bg)',
                border: `2px solid ${form.listing_type === k ? m.color : 'var(--border2)'}`,
                color: form.listing_type === k ? m.color : 'var(--text2)',
                borderRadius: 10, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >{m.icon} {t(m.label)}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: 14, marginBottom: 14 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('listings.field_title')} *</label>
          <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder={t('listings.title_ph')} maxLength={200} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('listings.field_year')}</label>
          <input className="form-input" type="number" value={form.year} onChange={e => set('year', e.target.value)} placeholder="2023" min={1900} max={2100} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('listings.field_condition')} *</label>
          <select className="form-input" value={form.condition} onChange={e => set('condition', e.target.value)}>
            {Object.entries(CONDITION_META).map(([k, m]) => (
              <option key={k} value={k}>{m.icon} {t(m.label)}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{priceLabel}</label>
          <input className="form-input" type="number" step="0.01" min="0" value={form.price} onChange={e => set('price', e.target.value)} placeholder="23" />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('listings.field_currency')}</label>
          <select className="form-input" value={form.currency} onChange={e => set('currency', e.target.value)}>
            {Object.entries(CURRENCY_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
        </div>
      </div>

      {isExchange && (
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">{t('listings.field_exchange_wish')} *</label>
          <input className="form-input" value={form.exchange_wish} onChange={e => set('exchange_wish', e.target.value)} placeholder={t('listings.exchange_wish_ph')} maxLength={300} />
        </div>
      )}

      {isAuction && (
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">{t('listings.field_auction_end')} *</label>
          <input className="form-input" type="datetime-local" value={form.auction_end} onChange={e => set('auction_end', e.target.value)} min={new Date(Date.now() + 3600000).toISOString().slice(0, 16)} />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
            {t('listings.auction_end_hint')}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('listings.field_city')}</label>
          <input className="form-input" value={form.city} onChange={e => set('city', e.target.value)} placeholder="Kaunas" maxLength={100} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label className="form-label">{t('listings.field_bgg')}</label>
          <input className="form-input" value={form.bgg_url} onChange={e => set('bgg_url', e.target.value)} placeholder="https://boardgamegeek.com/boardgame/..." />
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">{t('listings.field_description')}</label>
        <textarea className="form-textarea" rows={6} value={form.description} onChange={e => set('description', e.target.value)} placeholder={t('listings.description_ph')} maxLength={8000} />
      </div>

      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">{t('listings.field_shipping')}</label>
        <input className="form-input" value={form.shipping_info} onChange={e => set('shipping_info', e.target.value)} placeholder={t('listings.shipping_ph')} maxLength={500} />
      </div>

      <div className="form-group" style={{ marginBottom: 16 }}>
        <label className="form-label">{t('listings.field_images')}</label>
        <ImageUploader images={form.images} onChange={imgs => set('images', imgs)} />
      </div>

      {error && <div style={{ color: '#ff6060', fontSize: 13, marginBottom: 10 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? `⏳ ${t('listings.saving')}` : (initial?.id ? `💾 ${t('listings.save')}` : `📤 ${t('listings.publish')}`)}
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>{t('listings.cancel')}</button>
      </div>
    </div>
  );
}

// ── Skelbimo kortelė sąraše ─────────────────────────────────────────────────

function ListingCard({ listing, onClick, footer }) {
  const { t } = useTranslation();
  const img = listing.images?.[0] || PLACEHOLDER;
  const sold = listing.status === 'sold';
  return (
    <div
      onClick={onClick}
      style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'transform .15s, border-color .2s', opacity: sold ? 0.65 : 1 }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
    >
      <div style={{ position: 'relative', aspectRatio: '4/3', background: 'var(--bg)' }}>
        <img src={img} alt={listing.title} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {(listing.images?.length || 0) > 1 && (
          <span style={{ position: 'absolute', right: 8, bottom: 8, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 6 }}>
            📷 {listing.images.length}
          </span>
        )}
        {sold && (
          <span style={{ position: 'absolute', left: 8, top: 8, background: 'rgba(204,68,68,0.9)', color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 6 }}>{t('listings.sold').toUpperCase()}</span>
        )}
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {listing.title}{listing.year ? ` (${listing.year})` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
          <span style={{ fontFamily: 'Cinzel,serif', fontSize: 17, fontWeight: 800, color: '#f0c040' }}>
            {listing.listing_type === 'exchange' && !Number(listing.price)
              ? `🔄 ${t('listings.type_exchange')}`
              : formatPrice(listing.listing_type === 'auction' ? (listing.highest_bid ?? listing.price) : listing.price, listing.currency)}
          </span>
          <span style={{ display: 'inline-flex', gap: 5 }}>
            <TypeBadge type={listing.listing_type} size={10} />
            <ConditionBadge condition={listing.condition} size={11} />
          </span>
        </div>
        {listing.listing_type === 'auction' && (
          <div style={{ fontSize: 11, color: '#cc7744', fontWeight: 700, marginBottom: 6 }}>
            🔨 {t('listings.bids_count', { n: listing.bid_count || 0 })} · {timeLeftLt(listing.auction_end)}
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {listing.city && <span>📍 {listing.city}</span>}
          <span>🕒 {timeAgoLt(listing.created_at)}</span>
        </div>
        {footer}
      </div>
    </div>
  );
}

// ── Skelbimų sąrašas ────────────────────────────────────────────────────────

export default function ListingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [condition, setCondition] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [mine, setMine] = useState(false);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    if (mine && user) {
      api.get('/my/listings')
        .then(r => { setListings(r.data || []); setTotal((r.data || []).length); setPages(1); })
        .catch(() => setListings([]))
        .finally(() => setLoading(false));
      return;
    }
    const params = new URLSearchParams({ page: String(page), limit: '24' });
    if (condition) params.set('condition', condition);
    if (typeFilter) params.set('type', typeFilter);
    if (q) params.set('q', q);
    api.get(`/listings?${params}`)
      .then(r => { setListings(r.data.listings || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1); })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [condition, typeFilter, q, page, mine, user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    api.get('/my/listing-messages/unread').then(r => setUnread(r.data.unread || 0)).catch(() => {});
  }, [user]);

  const handleSaved = (listing) => {
    setShowForm(false); setEditing(null);
    navigate(`/skelbimai/${listing.id}`);
  };

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 20px' }}>
      {/* Antraštė */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <span style={{ fontSize: 32 }}>🛒</span>
              <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 28, color: '#f0c040', margin: 0, fontWeight: 700 }}>{t('listings.title')}</h1>
            </div>
            <p style={{ color: 'rgba(200,180,255,0.6)', fontSize: 14, margin: 0 }}>
              {t('listings.subtitle')}
            </p>
          </div>
          {user && !showForm && (
            <button className="btn btn-primary" onClick={() => { setEditing(null); setShowForm(true); }}>➕ {t('listings.new_title')}</button>
          )}
        </div>
      </div>

      {showForm && (
        <ListingForm initial={editing} onSaved={handleSaved} onCancel={() => { setShowForm(false); setEditing(null); }} />
      )}

      {/* Filtrai */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={qInput}
          onChange={e => setQInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { setQ(qInput); setPage(1); } }}
          placeholder={`🔍 ${t('listings.search_ph')}`}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '7px 12px', fontSize: 13, minWidth: 220 }}
        />
        <button className="btn btn-secondary btn-sm" onClick={() => { setQ(qInput); setPage(1); }}>{t('listings.search_btn')}</button>
        <select
          value={condition}
          onChange={e => { setCondition(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
        >
          <option value="">{t('listings.all_conditions')}</option>
          {Object.entries(CONDITION_META).map(([k, m]) => <option key={k} value={k}>{m.icon} {t(m.label)}</option>)}
        </select>
        <select
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '7px 12px', fontSize: 13, cursor: 'pointer' }}
        >
          <option value="">{t('listings.all_types')}</option>
          {Object.entries(LISTING_TYPE_META).map(([k, m]) => <option key={k} value={k}>{m.icon} {t(m.label)}</option>)}
        </select>
        {user && (
          <button
            onClick={() => { setMine(m => !m); setPage(1); }}
            style={{ background: mine ? 'rgba(240,192,64,0.18)' : 'var(--bg2)', border: `1px solid ${mine ? 'rgba(240,192,64,0.5)' : 'var(--border2)'}`, color: mine ? '#f0c040' : 'var(--text)', borderRadius: 8, padding: '7px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600, position: 'relative' }}
          >
            👤 {t('listings.my_listings')}
            {unread > 0 && (
              <span style={{ position: 'absolute', top: -7, right: -7, background: '#cc4444', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 999, minWidth: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{unread}</span>
            )}
          </button>
        )}
        {!loading && <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>{t('listings.count_label', { n: total })}</span>}
      </div>

      {/* Sąrašas */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>⏳ {t('listings.loading')}</div>
      ) : listings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🛒</div>
          <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--text3)' }}>
            {mine ? t('listings.empty_mine') : t('listings.empty')}
          </div>
          {user && !showForm && (
            <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setShowForm(true)}>{t('listings.create_first')}</button>
          )}
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
            {listings.map(l => (
              <ListingCard key={l.id} listing={l} onClick={() => navigate(`/skelbimai/${l.id}`)} />
            ))}
          </div>

          {pages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 32 }}>
              {Array.from({ length: pages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  style={{ background: n === page ? 'rgba(240,192,64,0.18)' : 'var(--bg2)', border: `1px solid ${n === page ? 'rgba(240,192,64,0.5)' : 'var(--border2)'}`, color: n === page ? '#f0c040' : 'var(--text)', borderRadius: 8, padding: '7px 13px', fontSize: 13, fontWeight: n === page ? 700 : 400, cursor: 'pointer', minWidth: 36 }}
                >{n}</button>
              ))}
            </div>
          )}
        </>
      )}

      {!user && (
        <div style={{ marginTop: 32, background: 'rgba(90,62,140,0.1)', border: '1px dashed rgba(90,62,140,0.4)', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>{t('listings.login_hint')}</div>
        </div>
      )}
    </div>
  );
}

// ── Dalinimosi mygtukai ─────────────────────────────────────────────────────

function ShareButtons({ title, imageUrl }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const eUrl = encodeURIComponent(url);
  const eTitle = encodeURIComponent(title);
  const img = imageUrl && imageUrl.startsWith('/') ? window.location.origin + imageUrl : (imageUrl || '');

  const links = [
    { icon: '📘', label: 'Facebook',  href: `https://www.facebook.com/sharer/sharer.php?u=${eUrl}` },
    { icon: '𝕏',  label: 'X',         href: `https://twitter.com/intent/tweet?url=${eUrl}&text=${eTitle}` },
    { icon: '💼', label: 'LinkedIn',  href: `https://www.linkedin.com/sharing/share-offsite/?url=${eUrl}` },
    { icon: '💬', label: 'WhatsApp',  href: `https://wa.me/?text=${eTitle}%20${eUrl}` },
    { icon: '📌', label: 'Pinterest', href: `https://pinterest.com/pin/create/button/?url=${eUrl}&media=${encodeURIComponent(img)}&description=${eTitle}` },
    { icon: '🤖', label: 'Reddit',    href: `https://www.reddit.com/submit?url=${eUrl}&title=${eTitle}` },
    { icon: '✉️', label: t('listings.share_email'), href: `mailto:?subject=${eTitle}&body=${eTitle}%0A${eUrl}` },
  ];

  const nativeShare = async () => {
    try { await navigator.share({ title, url }); } catch { /* atšaukta */ }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const btnStyle = { background: 'var(--bg2)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 };

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600 }}>{t('listings.share')}:</span>
      {links.map(l => (
        <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer" style={btnStyle} title={l.label}>
          {l.icon} <span className="share-label">{l.label}</span>
        </a>
      ))}
      {typeof navigator !== 'undefined' && navigator.share && (
        <button onClick={nativeShare} style={btnStyle} title={t('listings.share_more_title')}>📲 {t('listings.share_more')}</button>
      )}
      <button onClick={copy} style={{ ...btnStyle, color: copied ? '#44aa66' : 'var(--text)' }}>
        {copied ? `✅ ${t('listings.copied')}` : `🔗 ${t('listings.copy_link')}`}
      </button>
    </div>
  );
}

// ── Pokalbis su pardavėju / pirkėjais ───────────────────────────────────────

function ChatThread({ listingId, buyerId, user, onRead }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const boxRef = useRef(null);

  const load = useCallback(() => {
    const qs = buyerId ? `?buyer_id=${buyerId}` : '';
    api.get(`/listings/${listingId}/chat${qs}`)
      .then(r => { setMessages(r.data.messages || []); onRead?.(); })
      .catch(() => {});
  }, [listingId, buyerId, onRead]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true); setError('');
    try {
      const r = await api.post(`/listings/${listingId}/chat`, { message: msg, buyer_id: buyerId || 0 });
      setMessages(prev => [...prev, r.data]);
      setText('');
    } catch (e) {
      setError(e.response?.data?.detail || t('listings.chat_send_error'));
    } finally { setSending(false); }
  };

  return (
    <div>
      <div ref={boxRef} style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 2px', marginBottom: 10 }}>
        {messages.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: 16 }}>
            {t('listings.chat_empty')}
          </div>
        )}
        {messages.map(m => {
          const own = m.sender_id === user?.id;
          return (
            <div key={m.id} style={{ alignSelf: own ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
              <div style={{ background: own ? 'rgba(240,192,64,0.14)' : 'var(--bg)', border: `1px solid ${own ? 'rgba(240,192,64,0.35)' : 'var(--border2)'}`, borderRadius: 10, padding: '8px 12px', fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {m.message}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2, textAlign: own ? 'right' : 'left' }}>
                {m.sender_name} · {(m.created_at || '').slice(0, 16).replace('T', ' ')}
              </div>
            </div>
          );
        })}
      </div>
      {error && <div style={{ color: '#ff6060', fontSize: 12, marginBottom: 6 }}>⚠️ {error}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={t('listings.chat_ph')}
          rows={2}
          maxLength={2000}
          style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '8px 12px', fontSize: 13, resize: 'vertical' }}
        />
        <button className="btn btn-primary" onClick={send} disabled={sending || !text.trim()}>
          {sending ? '⏳' : '📤'}
        </button>
      </div>
    </div>
  );
}

function ListingChat({ listing, user }) {
  const { t } = useTranslation();
  const isSeller = user && user.id === listing.user_id;
  const [threads, setThreads] = useState([]);
  const [activeBuyer, setActiveBuyer] = useState(0);

  const loadThreads = useCallback(() => {
    if (!isSeller) return;
    api.get(`/listings/${listing.id}/chat/threads`)
      .then(r => {
        setThreads(r.data || []);
        if (r.data?.length && !activeBuyer) setActiveBuyer(r.data[0].buyer_id);
      })
      .catch(() => {});
  }, [listing.id, isSeller]); // eslint-disable-line

  useEffect(() => { loadThreads(); }, [loadThreads]);

  if (!user) {
    return (
      <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: 16 }}>
        🔐 {t('listings.chat_login')}
      </div>
    );
  }

  if (isSeller) {
    return (
      <div>
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
          {t('listings.seller_intro')}
        </div>
        {threads.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)', textAlign: 'center', padding: 16 }}>{t('listings.no_messages')}</div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              {threads.map(th => (
                <button
                  key={th.buyer_id}
                  onClick={() => setActiveBuyer(th.buyer_id)}
                  style={{ background: activeBuyer === th.buyer_id ? 'rgba(240,192,64,0.18)' : 'var(--bg)', border: `1px solid ${activeBuyer === th.buyer_id ? 'rgba(240,192,64,0.5)' : 'var(--border2)'}`, color: 'var(--text)', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer', position: 'relative' }}
                >
                  👤 {th.buyer_name}
                  {th.unread > 0 && (
                    <span style={{ marginLeft: 6, background: '#cc4444', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '1px 6px' }}>{th.unread}</span>
                  )}
                </button>
              ))}
            </div>
            {activeBuyer > 0 && (
              <ChatThread listingId={listing.id} buyerId={activeBuyer} user={user} onRead={loadThreads} />
            )}
          </>
        )}
      </div>
    );
  }

  return <ChatThread listingId={listing.id} buyerId={0} user={user} />;
}

// ── Aukcionas: statymai ─────────────────────────────────────────────────────

function AuctionPanel({ listing, user }) {
  const { t } = useTranslation();
  const [bids, setBids] = useState([]);
  const [amount, setAmount] = useState('');
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const load = useCallback(() => {
    api.get(`/listings/${listing.id}/bids`).then(r => setBids(r.data || [])).catch(() => {});
  }, [listing.id]);

  useEffect(() => { load(); }, [load]);

  const ended   = listing.auction_end && new Date(listing.auction_end.replace(' ', 'T')).getTime() <= Date.now();
  const highest = bids.length ? bids[0].amount : null;
  const minBid  = Math.max(highest || 0, Number(listing.price) || 0);
  const isOwner = user && user.id === listing.user_id;

  const place = async () => {
    if (placing) return;
    setPlacing(true); setError(''); setOk('');
    try {
      await api.post(`/listings/${listing.id}/bids`, { amount: Number(amount) });
      setOk(`✅ ${t('listings.bid_accepted')}`);
      setAmount('');
      load();
    } catch (e) {
      setError(e.response?.data?.detail || t('listings.bid_error'));
    } finally { setPlacing(false); }
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'Cinzel,serif', fontSize: 32, fontWeight: 800, color: '#cc7744' }}>
          🔨 {formatPrice(highest ?? listing.price, listing.currency)}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>
          {highest ? t('listings.highest_bid', { n: bids.length }) : t('listings.starting_price')}
        </span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: ended ? '#ff8080' : '#cc7744', margin: '6px 0 12px' }}>
        ⏱️ {timeLeftLt(listing.auction_end)}
      </div>

      {!ended && user && !isOwner && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="number" step="0.01" min={minBid + 0.01}
              value={amount} onChange={e => setAmount(e.target.value)}
              placeholder={`> ${minBid} ${CURRENCY_META[listing.currency]?.symbol || listing.currency}`}
              style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border2)', color: 'var(--text)', borderRadius: 8, padding: '8px 12px', fontSize: 14 }}
            />
            <button className="btn btn-primary" onClick={place} disabled={placing || !amount || Number(amount) <= minBid}>
              {placing ? '⏳' : `🔨 ${t('listings.bid_btn')}`}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{t('listings.min_bid', { price: formatPrice(minBid, listing.currency) })}</div>
        </div>
      )}
      {!ended && !user && (
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>🔐 {t('listings.auction_login')}</div>
      )}
      {error && <div style={{ color: '#ff6060', fontSize: 12, marginBottom: 8 }}>⚠️ {error}</div>}
      {ok && <div style={{ color: '#50e080', fontSize: 12, marginBottom: 8 }}>{ok}</div>}

      {bids.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{t('listings.bid_history')}</div>
          {bids.slice(0, 5).map((b, i) => (
            <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: i === 0 ? '#cc7744' : 'var(--text3)', fontWeight: i === 0 ? 700 : 400, padding: '3px 0' }}>
              <span>{i === 0 ? '🏆 ' : ''}{b.bidder_name}</span>
              <span>{formatPrice(b.amount, listing.currency)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Galerija ────────────────────────────────────────────────────────────────

function Gallery({ images, title }) {
  const [idx, setIdx] = useState(0);
  const imgs = images?.length ? images : [PLACEHOLDER];
  const prev = () => setIdx(i => (i - 1 + imgs.length) % imgs.length);
  const next = () => setIdx(i => (i + 1) % imgs.length);

  const arrowStyle = (side) => ({
    position: 'absolute', top: '50%', [side]: 10, transform: 'translateY(-50%)',
    background: 'rgba(0,0,0,0.55)', border: 'none', color: '#fff', borderRadius: 999,
    width: 38, height: 38, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
  });

  return (
    <div>
      <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: 'var(--bg)', border: '1px solid var(--border)' }}>
        <img
          src={imgs[idx]}
          alt={`${title} ${idx + 1}`}
          style={{ width: '100%', aspectRatio: '4/3', objectFit: 'contain', display: 'block', background: '#151020' }}
        />
        {imgs.length > 1 && (
          <>
            <button onClick={prev} style={arrowStyle('left')} aria-label="‹">‹</button>
            <button onClick={next} style={arrowStyle('right')} aria-label="›">›</button>
            <span style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, padding: '3px 12px', borderRadius: 999 }}>
              {idx + 1} / {imgs.length}
            </span>
          </>
        )}
      </div>
      {imgs.length > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {imgs.map((u, i) => (
            <img
              key={u + i}
              src={u}
              alt={`${title} ${i + 1}`}
              onClick={() => setIdx(i)}
              style={{ width: 68, height: 68, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: i === idx ? '2px solid #f0c040' : '1px solid var(--border2)', opacity: i === idx ? 1 : 0.7 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Skelbimo puslapis ───────────────────────────────────────────────────────

export function ListingDetailPage() {
  const { id } = useParams();
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [editing, setEditing] = useState(false);
  const [translated, setTranslated] = useState('');
  const [showTranslated, setShowTranslated] = useState(false);
  const [translating, setTranslating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/listings/${id}`)
      .then(r => setListing(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { setShowChat(false); setEditing(false); setTranslated(''); setShowTranslated(false); load(); }, [load]);

  const translate = async () => {
    if (translated) { setShowTranslated(s => !s); return; }
    setTranslating(true);
    try {
      const r = await api.post(`/listings/${id}/translate`, {}, { timeout: 90000 });
      setTranslated(r.data.description_lt || '');
      setShowTranslated(true);
    } catch { /* ignore */ } finally { setTranslating(false); }
  };

  const setStatus = async (status) => {
    await api.patch(`/listings/${id}/status`, { status });
    setListing(l => ({ ...l, status }));
  };

  const remove = async () => {
    if (!window.confirm(t('listings.confirm_delete'))) return;
    await api.delete(`/listings/${id}`);
    navigate('/skelbimai');
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 80, color: 'var(--text3)' }}>⏳ {t('listings.loading')}</div>;
  if (notFound || !listing) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🔍</div>
        <div style={{ fontFamily: 'Cinzel,serif', fontSize: 18, color: 'var(--text3)', marginBottom: 16 }}>{t('listings.not_found')}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/skelbimai')}>← {t('listings.title')}</button>
      </div>
    );
  }

  const isOwner = user && user.id === listing.user_id;
  const others = listing.seller_other_listings || [];
  const description = showTranslated && translated ? translated : listing.description;

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 20px' }}>
      <button className="btn btn-secondary btn-sm" style={{ marginBottom: 20 }} onClick={() => navigate('/skelbimai')}>← {t('listings.title')}</button>

      {editing && (
        <ListingForm
          initial={listing}
          onSaved={(l) => { setEditing(false); setListing(prev => ({ ...prev, ...l })); }}
          onCancel={() => setEditing(false)}
        />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 7fr) minmax(280px, 5fr)', gap: 28, alignItems: 'start' }} className="listing-detail-grid">
        {/* Kairė: galerija + aprašymas */}
        <div>
          <Gallery images={listing.images} title={listing.title} />

          {/* Aprašymas */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px', marginTop: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 16, color: 'var(--gold2)' }}>{t('listings.field_description')}</div>
              {listing.description && (
                <button className="btn btn-secondary btn-sm" onClick={translate} disabled={translating}>
                  {translating ? `⏳ ${t('listings.translating')}` : showTranslated ? `🌐 ${t('listings.show_original')}` : `🌐 ${t('listings.translate_btn')}`}
                </button>
              )}
            </div>
            <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0 }}>
              {description || t('listings.no_description')}
            </p>
            {listing.shipping_info && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 4 }}>📦 {t('listings.field_shipping')}</div>
                <div style={{ fontSize: 13, color: 'var(--text2)' }}>{listing.shipping_info}</div>
              </div>
            )}
            {listing.bgg_url && (
              <a href={listing.bgg_url} target="_blank" rel="noopener noreferrer"
                 style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 16, background: 'rgba(255,90,0,0.1)', border: '1px solid rgba(255,90,0,0.35)', color: '#ff8c40', borderRadius: 8, padding: '8px 14px', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                🎲 BoardGameGeek: {listing.title}{listing.year ? ` (${listing.year})` : ''} ↗
              </a>
            )}
          </div>

          {/* Įspėjimas apie sukčius */}
          <div style={{ background: 'rgba(204,68,68,0.07)', border: '1px solid rgba(204,68,68,0.35)', borderRadius: 12, padding: '18px 22px', marginTop: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#ff8080', marginBottom: 8 }}>⚠️ {t('listings.disclaimer_title')}</div>
            <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.7, margin: '0 0 10px' }}>{t('listings.disclaimer_p1')}</p>
            <p style={{ fontSize: 12.5, color: 'var(--text2)', lineHeight: 1.7, margin: 0 }}>{t('listings.disclaimer_p2')}</p>
          </div>
        </div>

        {/* Dešinė: kaina, pardavėjas, pokalbis */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '20px 24px' }}>
            <h1 style={{ fontFamily: 'Cinzel,serif', fontSize: 22, color: 'var(--text)', margin: '0 0 8px', fontWeight: 700 }}>
              {listing.title}{listing.year ? ` (${listing.year})` : ''}
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>🕒 {timeAgoLt(listing.created_at)}</span>
              <TypeBadge type={listing.listing_type} />
              <ConditionBadge condition={listing.condition} />
              {listing.status === 'sold' && (
                <span style={{ fontSize: 11, fontWeight: 800, color: '#ff8080', background: 'rgba(204,68,68,0.15)', border: '1px solid rgba(204,68,68,0.4)', borderRadius: 6, padding: '2px 10px' }}>{t('listings.sold').toUpperCase()}</span>
              )}
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>👁️ {listing.views}</span>
            </div>

            {listing.listing_type === 'auction' ? (
              <AuctionPanel listing={listing} user={user} />
            ) : listing.listing_type === 'exchange' ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: 'Cinzel,serif', fontSize: 26, fontWeight: 800, color: '#4488cc', marginBottom: 6 }}>🔄 {t('listings.type_exchange')}</div>
                {listing.exchange_wish && (
                  <div style={{ fontSize: 13, color: 'var(--text2)', background: 'rgba(68,136,204,0.08)', border: '1px solid rgba(68,136,204,0.3)', borderRadius: 8, padding: '8px 12px' }}>
                    <b>{t('listings.exchange_for')}:</b> {listing.exchange_wish}
                  </div>
                )}
                {Number(listing.price) > 0 && (
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6 }}>{t('listings.approx_value')}: {formatPrice(listing.price, listing.currency)}</div>
                )}
              </div>
            ) : (
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 32, fontWeight: 800, color: '#f0c040', marginBottom: 16 }}>
                {formatPrice(listing.price, listing.currency)}
              </div>
            )}

            {!isOwner && listing.status !== 'sold' && (
              <button className="btn btn-primary" style={{ width: '100%', marginBottom: 12 }} onClick={() => setShowChat(s => !s)}>
                💬 {listing.listing_type === 'exchange' ? t('listings.contact_exchange') : t('listings.contact_seller')}
              </button>
            )}
            {isOwner && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(true); window.scrollTo(0, 0); }}>✏️ {t('listings.edit')}</button>
                {listing.status === 'active'
                  ? <button className="btn btn-secondary btn-sm" onClick={() => setStatus('sold')}>✅ {t('listings.mark_sold')}</button>
                  : <button className="btn btn-secondary btn-sm" onClick={() => setStatus('active')}>🔄 {t('listings.mark_active')}</button>}
                <button className="btn btn-secondary btn-sm" style={{ color: '#ff8080' }} onClick={remove}>🗑️ {t('listings.delete')}</button>
              </div>
            )}
            {isAdmin && !isOwner && (
              <button className="btn btn-secondary btn-sm" style={{ color: '#ff8080', marginBottom: 12 }} onClick={remove}>🗑️ {t('listings.delete')} (admin)</button>
            )}

            <ShareButtons title={`${listing.title}${listing.year ? ` (${listing.year})` : ''} – ${formatPrice(listing.price, listing.currency)}`} imageUrl={listing.images?.[0]} />
          </div>

          {/* Pokalbis */}
          {(showChat || isOwner) && (
            <div style={{ background: 'var(--bg2)', border: '1px solid rgba(240,192,64,0.25)', borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, color: 'var(--gold2)', marginBottom: 12 }}>💬 {t('listings.chat_title')}</div>
              <ListingChat listing={listing} user={user} />
            </div>
          )}

          {/* Pardavėjas */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, color: 'var(--gold2)', marginBottom: 12 }}>{t('listings.seller')}</div>
            <Link to={`/players/${listing.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none' }}>
              {listing.seller_avatar
                ? <img src={listing.seller_avatar} alt={listing.seller_name} style={{ width: 46, height: 46, borderRadius: 999, objectFit: 'cover', border: '2px solid var(--border2)' }} />
                : <div style={{ width: 46, height: 46, borderRadius: 999, background: 'rgba(240,192,64,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>👤</div>}
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{listing.seller_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                  {[listing.seller_country || 'Lietuva', listing.seller_city || listing.city].filter(Boolean).join(', ')}
                </div>
              </div>
            </Link>
          </div>

          {/* Kiti pardavėjo skelbimai */}
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ fontFamily: 'Cinzel,serif', fontSize: 14, color: 'var(--gold2)', marginBottom: 12 }}>
              {t('listings.other_listings', { name: listing.seller_name })}
            </div>
            {others.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{t('listings.no_other')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {others.map(o => (
                  <div key={o.id} onClick={() => navigate(`/skelbimai/${o.id}`)}
                       style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: 6, borderRadius: 8, border: '1px solid transparent' }}
                       onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border2)'}
                       onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
                    <img src={o.images?.[0] || PLACEHOLDER} alt={o.title} style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.title}</div>
                      <div style={{ fontSize: 12, color: '#f0c040', fontWeight: 700 }}>{formatPrice(o.price, o.currency)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
